import React, { FC } from 'react';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { SectionWrapper } from '../SectionWrapper';
import { useTranslation } from 'react-i18next';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { PARTNER_FORM_FIELDS, PARTNER_FORM_FIELDS_NAMES } from '../../constants/new-partner/new-partner-form';
import { FormInput } from '../merchants/FormInput';
import { InputType } from '../form/form-input-constants';
import { UploadImage } from '../UploadImage';
import { PartnerDetailsUpdateComponent } from './PartnerDetailsUpdateComponent';
import { PARTNER_INFORMATION_INPUTS } from '../../constants/new-partner/partner-info-inputs';
import { IPartnerList } from '../../store/merchnts/merchant-interfaces';

interface Props {
	formik: any;
	uploadedFile: string;
	setUploadedFile: (value: string) => void;
	isEditMode: boolean;
	setIsEditMode: () => void;
	onSave: () => Promise<void>;
	onCancel: () => void;
	isLoading: boolean;
	modalState: IModalWindow;
	closeModal: () => void;
	partnerData: IPartnerList
}
const PartnerDetailsInformation: FC<Props> = (props) => {
	const {
		formik,
		uploadedFile,
		setUploadedFile,
		isEditMode,
		setIsEditMode,
		onSave,
		onCancel,
		isLoading,
		modalState,
		closeModal,
		partnerData
	} = props;
	const { t } = useTranslation();
	const { merchantState: { isPartnerDetailsDataLoading, merchantsLists }} = useMerchants();

	const siteUrl = formik.getFieldProps(PARTNER_FORM_FIELDS.SITE_URL).value;

	const policyUrl = formik.getFieldProps(PARTNER_FORM_FIELDS.PRIVACY_POLICY).value;

	const termUrl = formik.getFieldProps(PARTNER_FORM_FIELDS.TERMS).value;

	const merchantTermUrl = formik.getFieldProps(PARTNER_FORM_FIELDS.MERCHANT_TERMS).value;

	const INPUTS = PARTNER_INFORMATION_INPUTS({ t, lists: merchantsLists, partnerData });

	return (
		<>
			<SectionWrapper
				title={t('PARTNERS.PARTNER_INFO')}
				sectionAdditionalClass="mb-5 min-height-200"
				isEditMode={isEditMode}
				setIsEditMode={setIsEditMode}
				onEditClick={onSave}
				onCancelClick={onCancel}
				loading={isLoading}
				pageLoading={isPartnerDetailsDataLoading}
				isCardBody
				isEditBlock
			>
				<div className="row">
					{
						INPUTS.map(item => (
							<FormInput
								{...item}
								formik={formik}
								key={item.key}
								isEditMode={isEditMode}
							/>
						))
					}

					<UploadImage
						formik={formik}
						fieldName={PARTNER_FORM_FIELDS.LOGO}
						label={PARTNER_FORM_FIELDS_NAMES.LOGO}
						uploadedFile={uploadedFile}
						setUploadedFile={setUploadedFile}
						isEditMode={isEditMode}
					/>

					<FormInput
						formik={formik}
						key={PARTNER_FORM_FIELDS.SITE_URL}
						fieldName={PARTNER_FORM_FIELDS.SITE_URL}
						label={t(PARTNER_FORM_FIELDS_NAMES.SITE_URL)}
						placeholder={t(PARTNER_FORM_FIELDS_NAMES.SITE_URL)}
						link={siteUrl}
						inputType={InputType.Website}
						isEditMode={isEditMode}
						additionalClasses="col-12 col-sm-8 col-md-6"
						fakeInputAdditionalClass="col-12 col-sm-12 col-md-12"
					/>

					<div className="col-sm-6 col-md-12" />

					<FormInput
						formik={formik}
						key={PARTNER_FORM_FIELDS.PRIVACY_POLICY}
						fieldName={PARTNER_FORM_FIELDS.PRIVACY_POLICY}
						label={t(PARTNER_FORM_FIELDS_NAMES.PRIVACY_POLICY)}
						placeholder={t(PARTNER_FORM_FIELDS_NAMES.PRIVACY_POLICY)}
						link={policyUrl}
						inputType={InputType.Website}
						isEditMode={isEditMode}
						additionalClasses="col-12 col-sm-8 col-md-6"
						fakeInputAdditionalClass="col-12 col-sm-12 col-md-12"
					/>

					<div className="col-sm-6 col-md-12" />

					<FormInput
						formik={formik}
						key={PARTNER_FORM_FIELDS.TERMS}
						fieldName={PARTNER_FORM_FIELDS.TERMS}
						label={t(PARTNER_FORM_FIELDS_NAMES.TERMS)}
						placeholder={t(PARTNER_FORM_FIELDS_NAMES.TERMS)}
						link={termUrl}
						inputType={InputType.Website}
						isEditMode={isEditMode}
						additionalClasses="col-12 col-sm-8 col-md-6"
						fakeInputAdditionalClass="col-12 col-sm-12 col-md-12"
					/>

					<div className="col-sm-6 col-md-12" />

					<FormInput
						formik={formik}
						key={PARTNER_FORM_FIELDS.MERCHANT_TERMS}
						fieldName={PARTNER_FORM_FIELDS.MERCHANT_TERMS}
						label={t(PARTNER_FORM_FIELDS_NAMES.MERCHANT_TERMS)}
						placeholder={t(PARTNER_FORM_FIELDS_NAMES.MERCHANT_TERMS)}
						link={merchantTermUrl}
						inputType={InputType.Website}
						isEditMode={isEditMode}
						additionalClasses="col-12 col-sm-8 col-md-6"
						fakeInputAdditionalClass="col-12 col-sm-12 col-md-12"
					/>


					<FormInput
						formik={formik}
						key={PARTNER_FORM_FIELDS.TERMS_AND_CONDITIONS}
						fieldName={PARTNER_FORM_FIELDS.TERMS_AND_CONDITIONS}
						label={t('PARTNERS.LABEL.TERMS_AND_CONDITIONS')}
						placeholder={t('PARTNERS.LABEL.TERMS_AND_CONDITIONS')}
						inputType={InputType.Editor}
						isEditMode={isEditMode}
						additionalClasses="col-12 col-sm-8 col-md-6"
						fakeInputAdditionalClass="col-12 col-sm-12 col-md-12"
						showLink={true}
					/>

					<FormInput
						formik={formik}
						key={PARTNER_FORM_FIELDS.CONSENT_LABEL}
						fieldName={PARTNER_FORM_FIELDS.CONSENT_LABEL}
						label={t('PARTNERS.LABEL.CONSENT_LABEL')}
						placeholder={t('PARTNERS.LABEL.CONSENT_LABEL')}
						inputType={InputType.Description}
						isEditMode={isEditMode}
						additionalClasses="col-12 col-sm-8 col-md-6"
						fakeInputAdditionalClass="col-12 col-sm-12 col-md-12"
						showLink={true}
					/>
				</div>
			</SectionWrapper>

			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>

		</>
	);
};

export default PartnerDetailsUpdateComponent(PartnerDetailsInformation);
